import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import apiService from '../services/api';

// Components
import BacktestForm from '../components/BacktestForm';
import BacktestResults from '../components/BacktestResults';

const Backtest = () => {
  const [searchParams] = useSearchParams();
  const resultId = searchParams.get('result');
  const navigate = useNavigate();
  
  const [strategies, setStrategies] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [resultData, setResultData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [viewingResult, setViewingResult] = useState(false);

  // Fetch available strategies and symbols on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        
        // Fetch strategies
        const strategiesResponse = await apiService.backtest.getStrategies();
        setStrategies(strategiesResponse.data);
        
        // Fetch symbols
        const symbolsResponse = await apiService.common.getSymbols();
        setSymbols(symbolsResponse.data);
        
        // If resultId is provided AND we don't already have result data, fetch result data
        if (resultId && !resultData) {
          await fetchResultData(resultId);
          setViewingResult(true);
        }
      } catch (err) {
        console.error('Error fetching initial data:', err);
        setError('Failed to load initial data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialData();
  }, [resultId, resultData]);

  // Fetch result data by ID
  const fetchResultData = async (id) => {
    try {
      const response = await apiService.backtest.getResult(id);
      setResultData(response.data);
      return response.data;
    } catch (err) {
      console.error(`Error fetching result ${id}:`, err);
      setError(`Failed to load result #${id}. Please try again later.`);
    }
  };

  // Handle running a new backtest
  const handleRunBacktest = async (backtestParams) => {
    try {
      setRunning(true);
      setError(null);
      
      const response = await apiService.backtest.run(backtestParams);
      
      // Check if we got valid result data with an ID
      if (!response.data || response.data.id === null) {
        throw new Error('Backtest completed but the result could not be saved to the database');
      }
      
      setResultData(response.data);
      setViewingResult(true);
      
      // Update URL with result ID
      navigate(`/backtest?result=${response.data.id}`);
    } catch (err) {
      console.error('Error running backtest:', err);
      
      // Display more specific error messages based on the error
      if (err.response && err.response.data && err.response.data.detail) {
        setError(`Failed to run backtest: ${err.response.data.detail}`);
      } else if (err.message) {
        setError(`Failed to run backtest: ${err.message}`);
      } else {
        setError('Failed to run backtest. Please check your parameters and try again.');
      }
    } finally {
      setRunning(false);
    }
  };

  // Handle going back to form
  const handleBackToForm = () => {
    setViewingResult(false);
    setResultData(null);
    navigate('/backtest');
  };

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          {viewingResult ? 'Backtest Results' : 'Run Backtest'}
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          {viewingResult 
            ? `Viewing results for ${resultData?.strategy_name} on ${resultData?.symbol.toUpperCase()}`
            : 'Configure and run a backtest for an options strategy.'}
        </p>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 m-4 rounded relative" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {loading ? (
        <div className="px-4 py-5 sm:p-6 text-center">
          <p className="text-gray-500">Loading...</p>
        </div>
      ) : viewingResult && resultData ? (
        <BacktestResults 
          result={resultData} 
          onBackToForm={handleBackToForm} 
        />
      ) : (
        <BacktestForm 
          strategies={strategies} 
          symbols={symbols} 
          onSubmit={handleRunBacktest} 
          isRunning={running} 
        />
      )}
    </div>
  );
};

export default Backtest;
